import { CustomLink } from '@/components/custom-link/CustomLink';
import {
  NavbarItemTypeEnum,
  type NavbarItem,
  type NavbarMenuItem,
} from '@/shared/constant/navbar/NavbarItem';
import { isClient } from '@/shared/util/ssr';
import { NavLinkTargetOptions } from '@tarslib/types/lib/publishedProject';
import { Dropdown } from 'aui';
import { FoxIconArrowNarrowUpRight, FoxIconDownMd, FoxIconRightMd } from 'aui-biz-icons';
import type { ItemType } from 'aui/lib/menu/hooks/useItems';
import clsx from 'clsx';
import React from 'react';
import type { NavbarGroupTypeLevel } from '../../type/NavbarGroupTypeLevel';
import { NavbarGroupLevel, NavbarGroupType } from '../../type/NavbarGroupTypeLevel';
import { IconLabel } from '../icon-label/IconLabel';
import styles from './NavbarElement.module.css';

export type NavbarElementProps = {
  value: NavbarItem;
  groupLevelType: NavbarGroupTypeLevel;
  isMenuChild: boolean;
  onClick?: (e: React.MouseEvent<HTMLAnchorElement>) => void;
  subfix?: React.ReactNode;
  className?: string;
};

function NavbarMenuElement(props: NavbarElementProps & { value: NavbarMenuItem }) {
  const { value, isMenuChild, onClick, className } = props;

  const [isOpen, setIsOpen] = React.useState(false);
  const [showMenuChild, setShowMenuChild] = React.useState(true);

  const navbarItemToItemType = (x: NavbarItem): ItemType => {
    const selected = x.type === NavbarItemTypeEnum.Link ? false : x.selected;
    const isSameOrigin =
      x.type === NavbarItemTypeEnum.Link && isClient && x.to.startsWith(window.location.origin);
    const target = (() => {
      if (x.type === NavbarItemTypeEnum.Link) {
        return x.target;
      }
      return undefined;
    })();
    const subfixIcon = (() => {
      if (x.type === NavbarItemTypeEnum.Link && !isSameOrigin) {
        return <FoxIconArrowNarrowUpRight size={12} className="text-disabled" />;
      }

      return undefined;
    })();
    const to = (() => {
      if (x.type === NavbarItemTypeEnum.Menu) {
        return '';
      }
      return x.to;
    })();
    const disabled = (() => {
      if (x.type === NavbarItemTypeEnum.Menu) {
        return x.disabled || !x.children.length;
      }

      return x.disabled || !to;
    })();

    return {
      key: x.id,
      disabled,
      children:
        x.type === NavbarItemTypeEnum.Menu
          ? x.children.map((y) => navbarItemToItemType(y))
          : undefined,
      title: x.title,
      label: (
        <CustomLink
          to={to}
          target={target}
          disabled={x.disabled}
          onClick={(e) => {
            setIsOpen(false);
            onClick?.(e);
          }}
        >
          <IconLabel
            className="w-[192px]"
            icon={x.icon}
            label={x.title}
            selected={selected}
            subfix={subfixIcon}
          />
        </CustomLink>
      ),
    };
  };

  return (
    <Dropdown
      open={isOpen}
      onOpenChange={setIsOpen}
      overlayClassName={clsx('block max-os:hidden', styles.dropdown)}
      destroyPopupOnHide
      menu={{
        items: value.children.map((x) => navbarItemToItemType(x)),
      }}
    >
      <div
        className={clsx('flex-shrink-0 cursor-pointer max-os:w-full', className)}
        title={value.title}
      >
        <NavbarElement
          value={{
            ...value,
            type: NavbarItemTypeEnum.Folder,
            to: '',
          }}
          groupLevelType={props.groupLevelType}
          isMenuChild={isMenuChild}
          subfix={
            <>
              <FoxIconDownMd className="hidden os:inline-block" size={16} />
              {showMenuChild ? (
                <FoxIconDownMd className="inline-block os:hidden" size={16} />
              ) : (
                <FoxIconRightMd className="inline-block os:hidden" size={16} />
              )}
            </>
          }
          onClick={() => {
            setShowMenuChild(!showMenuChild);
          }}
        />

        <ul
          className={clsx('hidden pl-6', {
            ['max-os:block']: showMenuChild,
          })}
        >
          {value.children.map((x) => {
            return (
              <li key={x.id}>
                <NavbarElement {...props} value={x} isMenuChild onClick={onClick} />
              </li>
            );
          })}
        </ul>
      </div>
    </Dropdown>
  );
}

export function NavbarElement(props: NavbarElementProps) {
  const { value, groupLevelType, isMenuChild, subfix, onClick, className } = props;
  const isMaxMdShowIconBorder =
    groupLevelType.level !== NavbarGroupLevel.Level1 &&
    groupLevelType.type !== NavbarGroupType.Select;
  const selected = value.type === NavbarItemTypeEnum.Link ? false : value.selected;
  const [isSameOrigin, setIsSameOrigin] = React.useState(false);

  React.useEffect(() => {
    if (value.type === NavbarItemTypeEnum.Link) {
      if (value.to.startsWith(window.location.origin)) {
        setIsSameOrigin(true);
      }
    }
  }, [value]);

  const target = React.useMemo(() => {
    if (value.type === NavbarItemTypeEnum.Link) {
      if (isSameOrigin) {
        return NavLinkTargetOptions.SELF;
      } else {
        return value.target;
      }
    }

    return undefined;
  }, [isSameOrigin, value]);

  if (value.type === NavbarItemTypeEnum.Menu) {
    if (isMenuChild) {
      return <></>;
    }

    return <NavbarMenuElement {...props} value={value} />;
  }

  return React.createElement(
    value.to && !value.disabled ? CustomLink : 'span',
    {
      to: value.to,
      disabled: value.disabled,
      className: clsx(
        'relative inline-flex max-w-full flex-shrink-0 items-center os:max-w-[192px]',
        groupLevelType.level === NavbarGroupLevel.Level3 ? 'px-2.5 py-0.5' : 'px-3 py-[5px]',
        isMaxMdShowIconBorder ? 'max-os:px-0 max-os:py-1' : 'max-os:py-2',
        selected &&
          groupLevelType.level === NavbarGroupLevel.Level3 &&
          'os:rounded-lg os:bg-primary-1',
        {
          ['cursor-not-allowed']: value.disabled,
        },
        className,
      ),
      target,
      onClick: onClick,
      title: value.title,
    },
    <>
      <IconLabel
        icon={value.icon}
        label={value.title}
        selected={selected}
        isMaxMdShowIconBorder={isMaxMdShowIconBorder}
        isBold={
          !isMenuChild &&
          groupLevelType.level === NavbarGroupLevel.Level1 &&
          groupLevelType.type === NavbarGroupType.Default
        }
        subfix={
          subfix ??
          (value.type === NavbarItemTypeEnum.Link && !isSameOrigin ? (
            <FoxIconArrowNarrowUpRight size={12} className="text-disabled" />
          ) : undefined)
        }
      />

      {selected &&
        groupLevelType.level !== NavbarGroupLevel.Level3 &&
        groupLevelType.type === NavbarGroupType.LineTab && (
          <div className="absolute bottom-[-9px] left-0 h-[2px] w-full rounded-t-full bg-primary max-os:hidden" />
        )}
    </>,
  );
}
